import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog } from 'radix-ui';
import { TextInput } from '@producer/ui-kit';

import { AddCover } from '../../../../components/add-file/add-cover';
import { ProjectTemplateItem, ProjectTemplateType } from './project-template-item';
import { formatPicture } from '../../../../core/services/image.service';
import { ModalHeader } from '../../../../components/form-builder-modal/modal-header';
import { HasServerResponse } from '../../../../components/PanelContainer';
import { Alert } from '../../../../components/alert/Alert';
import { SelectionProvider } from '../../../../core/contexts/selection.context';
import { projectTemplates } from './desktop-create-project.modal';
import { PageLoader } from '../../../../components/page-loader/PageLoader';

import styles from './mobile-create-project-modal.module.scss';
interface MobileCreateProjectModalProps {
  spaceId: string;
  isOpen: boolean;
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
}

export const MobileCreateProjectModal: React.FC<MobileCreateProjectModalProps> = ({
  isOpen,
  onSubmit,
  onCancel,
  spaceId,
}) => {
  const { t } = useTranslation('createProject');
  const [file, setFile] = useState<File | null>();
  const [projectTemplate, setProjectTemplate] = useState(ProjectTemplateType.Commercial);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<any>();
  const [fileError, setFileError] = useState(null);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .transform((value) => value.trim())
          .matches(/^(.*)?\S+(.*)?$/, t('common:fieldRequired'))
          .required(t('common:fieldRequired'))
          .max(32, t('common:fieldMax', { max: 32 })),
        client: yup.string().max(32, t('common:fieldMax', { max: 32 })),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      client: '',
      template: projectTemplate,
    },
  });

  const watchedName = watch('name');

  useEffect(() => {
    setValue('template', projectTemplate);

    const defaultDemoName = 'Breitling - History (Demo)';
    if (projectTemplate === ProjectTemplateType.Corporate) {
      if (!watchedName) {
        setValue('name', defaultDemoName);
      }
    } else {
      if (watchedName === defaultDemoName) {
        setValue('name', '');
      }
    }
  }, [projectTemplate]);

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      onCancel();
    }
  };

  const onHandleSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    setFileError(null);
    setIsLoading(true);

    let frame;
    if (file) {
      const options = { width: 640, height: 380 };
      frame = await formatPicture(file, options);
    }

    try {
      await onSubmit({
        spaceId,
        ...values,
        ...(frame && { file: frame }),
      });

      onCancel();
    } catch (e) {
      setServerError(e);
    }
  };

  return (
    <Dialog.Root key={'create-project'} open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles['overlay']} />

        <Dialog.Content className={`${styles['content']} ${'DialogContent'}`}>
          <ModalHeader
            title={'Create project'}
            onClose={onCancel}
            action={'Create'}
            onAction={handleSubmit(onHandleSubmit)}
          />

          <div className={styles['container']}>
            {serverError && serverError.message && (
              <HasServerResponse>
                <Alert text={serverError.message} />
              </HasServerResponse>
            )}

            <div className={styles['form']}>
              <div className={styles['inputs-wrapper']}>
                <TextInput
                  {...register('name')}
                  errorMessage={errors.name?.message}
                  label={t('name')}
                  placeholder={t('name_placeholder')}
                  required
                />

                <TextInput
                  {...register('client')}
                  name={'client'}
                  label={t('client')}
                  placeholder={t('client_placeholder')}
                  autocomplete="false"
                  errorMessage={errors.client?.message}
                />
              </div>

              <div className={styles['add-cover-wrapper']}>
                <AddCover
                  setFieldValue={(_, file) => setFile(file)}
                  errorMessage={fileError}
                  file={file}
                  acceptedFiles={['.png', '.jpeg']}
                />
              </div>
            </div>

            <div className={styles['project-templates-container']}>
              <p>Use a demo or a template to get started</p>

              <div className={styles['project-templates']}>
                <SelectionProvider items={projectTemplates.map((el) => el.value)}>
                  {projectTemplates.map((template) => (
                    <ProjectTemplateItem
                      key={template.value}
                      category={template.category}
                      value={template.value as ProjectTemplateType}
                      defaultValue={ProjectTemplateType.Commercial}
                      badge={template.badge as 'Template' | 'Demo'}
                      backgroundImage={template.backgroundImage}
                      onSelect={() => setProjectTemplate(template.value)}
                    />
                  ))}
                </SelectionProvider>
              </div>
            </div>
          </div>

          {isLoading && (
            <div className={styles['loading-overlay']}>
              <PageLoader />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
